import React, { useState, useEffect } from 'react';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaNewspaper, FaUser, FaClock, FaCheck, FaTimes } from 'react-icons/fa';
import '../pages_premium.css';
import { getFullImageUrl } from '../utils/url';

const safeParseImages = (imagesVal) => {
  if (!imagesVal) return [];
  if (Array.isArray(imagesVal)) return imagesVal;
  try {
    const parsed = typeof imagesVal === 'string' ? JSON.parse(imagesVal) : imagesVal;
    if (Array.isArray(parsed)) return parsed;
  } catch (e) {
    console.warn('Failed to parse blog images', e);
  }
  return [];
};

const getImgUrl = (img) => {
  return getFullImageUrl(img);
};

function BlogView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBlogDetails = async () => {
            try {
                const res = await axiosInstance.get(`/blogs/${id}`);
                setBlog(res.data);
            } catch (error) {
                console.error('Error fetching blog details:', error);
                toast.error('Failed to load blog details');
            } finally {
                setLoading(false);
            }
        };
        fetchBlogDetails();
    }, [id]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading Details...</span>
                </div>
            </div>
        );
    }

    if (!blog) {
        return (
            <div className="container py-5 text-center text-muted">
                <h3>Blog post not found.</h3>
                <button className="btn btn-primary mt-3" onClick={() => navigate('/blogs')}>Back to Blogs</button>
            </div>
        );
    }

    const images = safeParseImages(blog.images);
    const formattedDate = blog.createdAt ? new Date(blog.createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    }) : 'N/A';

    return (
        <div className="page-premium-wrapper">
            <div className="page-premium-content">

                {/* Hero Header */}
                <div className="page-hero-header page-hero-properties" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                    <div className="ph-blob ph-blob-1"></div>
                    <div className="ph-blob ph-blob-2"></div>

                    <div className="ph-left">
                        <div className="ph-icon">
                            <FaNewspaper size={28} />
                        </div>
                        <div>
                            <h1 className="ph-title">{blog.title}</h1>
                            <p className="ph-subtitle">Published Guide · ID: #{blog.id}</p>
                        </div>
                    </div>

                    <div className="ph-right">
                        <span className={`badge text-capitalize ${
                            blog.status === 'published' ? 'badge-success' : 'badge-warning'
                        }`} style={{ padding: '8px 16px', borderRadius: '50px', fontSize: '13px' }}>
                            {blog.status === 'published' ? 'Published' : 'Draft'}
                        </span>
                    </div>
                </div>

                {/* Back button */}
                <div className="mb-4">
                    <button className="ac-back-btn m-0" onClick={() => navigate('/blogs')}>
                        <FaArrowLeft /> Back to Blogs
                    </button>
                </div>

                {/* Vertical Column Stack */}
                <div className="card border-0 shadow-sm rounded-24 overflow-hidden bg-white p-4">
                    
                    {/* 1. Image Card */}
                    {images.length > 0 && (
                        <div className="rounded-20 overflow-hidden mb-4 border bg-light w-100" style={{ height: '400px' }}>
                            <img 
                                src={getImgUrl(images[0])} 
                                alt={blog.title} 
                                className="w-100 h-100 object-cover" 
                            />
                        </div>
                    )}

                    {/* 2. Article Details (Horizontal Grid Card) */}
                    <div className="bg-light rounded-20 p-4 mb-4 border border-gray-100">
                        <div className="row">
                            {/* Author details */}
                            <div className="col-md-4 mb-3 mb-md-0 border-right border-gray-200">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="bg-white rounded-circle d-flex align-items-center justify-content-center text-primary shadow-sm" style={{ width: '46px', height: '46px', flexShrink: 0 }}>
                                        <FaUser size={16} />
                                    </div>
                                    <div>
                                        <span className="text-muted small text-uppercase font-weight-bold d-block" style={{ fontSize: '10px', letterSpacing: '0.05em' }}>Author</span>
                                        <strong className="text-dark d-block" style={{ fontSize: '14px' }}>{blog.Author ? blog.Author.name : 'Administrator'}</strong>
                                        <small className="text-muted d-block truncate" style={{ maxWidth: '180px' }}>{blog.Author ? blog.Author.email : 'system@studentsunion.au'}</small>
                                    </div>
                                </div>
                            </div>

                            {/* Publish Date */}
                            <div className="col-md-4 mb-3 mb-md-0 border-right border-gray-200">
                                <div className="d-flex align-items-center gap-3 pl-0 pl-md-3">
                                    <div className="bg-white rounded-circle d-flex align-items-center justify-content-center text-success shadow-sm" style={{ width: '46px', height: '46px', flexShrink: 0 }}>
                                        <FaClock size={16} />
                                    </div>
                                    <div>
                                        <span className="text-muted small text-uppercase font-weight-bold d-block" style={{ fontSize: '10px', letterSpacing: '0.05em' }}>Published Date</span>
                                        <strong className="text-dark d-block" style={{ fontSize: '14px' }}>{formattedDate}</strong>
                                    </div>
                                </div>
                            </div>

                            {/* Status info */}
                            <div className="col-md-4">
                                <div className="d-flex align-items-center gap-3 pl-0 pl-md-3">
                                    <div className="bg-white rounded-circle d-flex align-items-center justify-content-center text-warning shadow-sm" style={{ width: '46px', height: '46px', flexShrink: 0 }}>
                                        {blog.status === 'published' ? <FaCheck className="text-success" size={16} /> : <FaTimes className="text-warning" size={16} />}
                                    </div>
                                    <div>
                                        <span className="text-muted small text-uppercase font-weight-bold d-block" style={{ fontSize: '10px', letterSpacing: '0.05em' }}>Publishing Status</span>
                                        <strong className="text-dark text-capitalize d-block" style={{ fontSize: '14px' }}>{blog.status}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 3. Description / Content Body */}
                    <div className="px-2 mt-2">
                        <h4 className="font-weight-black text-dark mb-3">Content / Description</h4>
                        <div className="ac-divider mb-3" style={{ opacity: 0.3 }}></div>
                        <p className="text-secondary" style={{ whiteSpace: 'pre-line', lineHeight: '1.9', fontSize: '16px', color: '#4b5563' }}>
                            {blog.content}
                        </p>
                    </div>
                </div>

            </div>
        </div>
    );
}

export default BlogView;
